import { createSelector } from 'reselect';
import {
  isAudible as isContentAudible,
  isVisible as isContentVisible,
} from 'content-creation-common/utils/content';
import { FrameRange } from 'content-creation-common/models';

import * as ContentSelectors from './content';
import {
  CONTENT_TYPE,
  PLAYBACK_FRAMES_PER_SECOND,
  TEMPLATE_FILTER_SIZES,
} from '../constants';

import { GROUP_ROLES } from '../features/group/constants';
import { LOCKABLE_TRANSFORMS } from '../features/layer-manipulation/constants';
import Layer from '../model/Layer';
import { isGroup as isGroupContentType } from '../features/group/util';
import { info } from '../util/log';
import {
  isEmpty as isEmptyObject,
  isNil,
  isNotNil,
  isArray,
} from '../util/object';
import {
  isEmpty as isEmptyArray,
  isNotEmpty as isNotEmptyArray,
} from '../util/array';
import {
  ALPHA_BLEND_MODES,
  DEFAULT_ALPHA_BLEND_MODE,
} from '../constants/AlphaBlendModes';
import { createKeyedStateSelector } from '../features/shared/utils/selectorUtils';
import { inSimpleFlow } from '../features/routes/state-management/selectors';
import { hasAudio } from '../features/media/util';
import { getCurrentFrame } from '../features/playback/manager/PlaybackManager';

const getAllContentById = (state) => state.content.contentById;
const getAllMediaById = (state) => state.media.mediaById;
export const getAllElementsById = (state) => state.elements.elementsById;
export const getDraggingDirection = (state) => state.elements.direction;
export const getElementToDelete = (state) => state.elements.toDelete;
export const getMoveToElementId = (state) => state.elements.moveToElementId;
export const getRootIds = (state) => state.elements.elements;
export const getRootIdsReversed = createSelector([getRootIds], (allIds) =>
  [...allIds].reverse(),
);

// @todo(hamik): move into another file? https://jira.sc-corp.net/browse/ADS-37067
export const getByIdHelper = (elementMap, elementId) => {
  if (isNil(elementId)) return null;
  const element = elementMap[elementId];
  if (!element) {
    info(`No element found with id ${elementId}`);
    return null;
  }
  return element;
};

export const getById = createKeyedStateSelector(
  [getAllElementsById, (state, elementId) => elementId],
  (elementMap, elementId) => getByIdHelper(elementMap, elementId),
);

// @todo(hamik): move into another file? https://jira.sc-corp.net/browse/ADS-37067
export const getContentIdHelper = (element) => {
  if (!element) {
    return null;
  }
  const { contentId } = element;
  return contentId;
};

export const getContentId = createKeyedStateSelector([getById], (element) =>
  getContentIdHelper(element),
);

export const getIsTransformedByUser = createKeyedStateSelector(
  [getById],
  (element) => {
    if (!element) {
      return null;
    }

    const { isTransformedByUser } = element;
    return isTransformedByUser;
  },
);

export const getContentHelper = (contentId, contentById) => {
  if (isNil(contentId)) return null;
  const content = contentById[contentId];
  if (!content) {
    info(`No content found with id ${contentId}`);
    return null;
  }
  return content;
};

export const getContent = createKeyedStateSelector(
  [getContentId, getAllContentById],
  (contentId, contentById) => getContentHelper(contentId, contentById),
);

export const isVisible = createKeyedStateSelector([getContent], (content) => {
  return isContentVisible(content);
});

export const getIsTransformLockedHelper = (element, transform) => {
  if (isNil(element) || isNil(element.locked)) return false;
  return element.locked[transform];
};

export const getIsAllTransformsLocked = createKeyedStateSelector([getById], (element) => {
  // Check that there are locked transforms and that they are all true
  if (isNil(element) || isNil(element.locked)) return false;
  const lockedValues = Object.values(element.locked);
  return lockedValues.length > 0 && lockedValues.every((i) => { return i === true; });
});

export const getIsScaleLocked = createKeyedStateSelector([getById], (element) => {
  return getIsTransformLockedHelper(element, LOCKABLE_TRANSFORMS.SCALE);
});

export const getIsRotationLocked = createKeyedStateSelector([getById], (element) => {
  return getIsTransformLockedHelper(element, LOCKABLE_TRANSFORMS.ROTATION);
});

export const getIsTranslationLocked = createKeyedStateSelector([getById], (element) => {
  return getIsTransformLockedHelper(element, LOCKABLE_TRANSFORMS.TRANSLATION);
});

export const getAllIds = createSelector(
  [getRootIds, getAllElementsById, getAllContentById],
  (rootIds, elementMap, contentMap) => {
    const allIds = rootIds.reduce(function addElementIds(list, elementId) {
      // Check for existence of element.
      const element = elementMap[elementId];
      if (isNil(element)) return list;

      // Push id on to list
      list.push(elementId);

      // If there is content and its a group, lets recurse
      const { contentId } = element;
      const content = contentMap[contentId];
      if (isNil(content)) return list;
      if (!isGroupContentType(content.contentType)) return list;

      const { contentElementIds } = content;
      contentElementIds.reduce((list, contentElementId) => {
        return addElementIds(list, contentElementId);
      }, list);

      return list;
    }, []);

    return allIds;
  },
);

export const getAllIdsReversed = createSelector([getAllIds], (allIds) =>
  [...allIds].reverse(),
);

export const getAllElements = createSelector(
  [getAllIds, getAllElementsById],
  (allIds, elementsById) => {
    return allIds.map((id) => elementsById[id]);
  },
);

const getTemplateProxy = (state) => {
  if (isNil(state.ui)) return null;
  return state.ui.template;
};

/**
 * @function getAllLayers
 * @return {Array<Layer>}
 */
// todo clean up reading state.ui.template
// cannot import template() from ui selectors as it would cause circular dependency
export const getAllLayers = createSelector(
  [getAllElements, getAllContentById, getAllMediaById, getTemplateProxy],
  (elements, contentById, mediaById, template) => {
    const validElements = elements.filter(({ contentId }) => {
      // It is currently presumed all elements will have a content id.
      if (isNil(contentId)) return false;

      // Furthermore, each content id should correspond to a content object.
      // If not, we don't want to mess with the element.
      const content = contentById[contentId];
      return isNotNil(content);
    });

    return validElements.map((element) => {
      const { contentId } = element;
      const content = contentById[contentId];
      const { mediaId } = content;
      const media = mediaById[mediaId];
      return Layer.getLayerFromReduxStateObjects(
        element,
        content,
        media,
        template,
      );
    });
  },
);

export const getAllLayersByMediaServerId = createKeyedStateSelector(
  [getAllLayers, (state, mediaServerId) => mediaServerId],
  (allLayers, mediaServerId) => {
    if (isEmptyArray(allLayers)) return allLayers;
    return allLayers.filter((layer) => layer.mediumServerId === mediaServerId);
  },
);

/**
 * Get a Map of layers, keyed by their layer ID.
 * @returns {Map}
 */
export const getAllLayersById = createSelector([getAllLayers], (layers) => {
  return layers.reduce((all, layer) => {
    all.set(layer.id, layer);
    return all;
  }, new Map());
});

export const getAllElementsReversed = createSelector(
  [getAllIdsReversed, getAllElementsById],
  (allIds, elementsById) => allIds.map((id) => elementsById[id]),
);

export const isEditingTransform = createKeyedStateSelector(
  [getById],
  (element) => {
    if (!element) {
      return false;
    }

    return element.isEditingTransform;
  },
);

export const isEditingTransformOfAnyElement = createSelector(
  [getAllElements],
  (allElements) => {
    if (isEmptyArray(allElements)) {
      return false;
    }

    const transformingElement = allElements.find((element) => {
      return isNotNil(element) && element.isEditingTransform;
    });

    return isNotNil(transformingElement);
  },
);

export const getContentType = createKeyedStateSelector(
  [getContent],
  (content) => {
    if (isNil(content)) return null;

    const { contentType } = content;
    return contentType;
  },
);

export const isContentType = createSelector(
  [
    getContentType,
    (state, elementId, expectedContentType) => expectedContentType,
  ],
  (contentType, expectedContentType) => {
    if (isNil(contentType)) return false;

    return contentType === expectedContentType;
  },
);

export const getCreativeFieldId = createKeyedStateSelector(
  [getContent],
  (content) => {
    return !isEmptyObject(content) ? content.creativeFieldId : null;
  },
);

export const getMediaHelper = (
  elementMap,
  elementId,
  contentById,
  mediaById,
) => {
  const element = getByIdHelper(elementMap, elementId);
  if (isNil(element)) return null;
  const contentId = getContentIdHelper(element);
  if (isNil(contentId)) return null;
  return ContentSelectors.getMediaHelper(contentById, contentId, mediaById);
};

// @todo(hamik): maybe turn into a keyed selector. waiting for https://jira.sc-corp.net/browse/ADS-37067
export const getMedia = createKeyedStateSelector(
  [
    getAllElementsById,
    getAllContentById,
    getAllMediaById,
    (state, elementId) => elementId,
  ],
  (elementMap, contentById, mediaById, elementId) =>
    getMediaHelper(elementMap, elementId, contentById, mediaById),
);

export const getMediaId = createKeyedStateSelector([getContent], (content) => {
  if (!content) {
    return null;
  }

  const { mediaId } = content;
  return mediaId;
});

export const getContentWidth = (state, elementId) => {
  const contentId = getContentId(state, elementId);
  if (!contentId) {
    return null;
  }

  return ContentSelectors.getWidth(state, contentId);
};

export const getContentHeight = (state, elementId) => {
  const contentId = getContentId(state, elementId);
  if (!contentId) {
    return null;
  }

  return ContentSelectors.getHeight(state, contentId);
};

export const getParentElementId = createKeyedStateSelector(
  [getById],
  (element) => element && element.groupId,
);

// We can use Root Ids for this, because otherwise how do you have anything nested?
export const atLeastOneAdded = createSelector([getRootIds], (rootIds) => {
  return isNotEmptyArray(rootIds);
});

export const getRange = createKeyedStateSelector([getById], (element) => {
  if (!element) {
    return null;
  }

  return new FrameRange(element.frameStart, element.frameEnd);
});

export const getActiveRange = (state, elementId) => {
  const range = getRange(state, elementId);
  if (isNil(range)) {
    return null;
  }

  // If element has parent element, check its active status.
  const parentId = getParentElementId(state, elementId);
  // If not parent, return true (since we'd've already returned false above)
  if (isNil(parentId)) {
    return range;
  }

  const { start, end } = range;

  const parentRange = getActiveRange(state, parentId);
  const { start: parentFrameStart, end: parentFrameEnd } = parentRange;

  const finalFrameStart = Math.max(start, parentFrameStart);
  const finalFrameEnd = Math.min(end, parentFrameEnd);

  if (finalFrameStart > finalFrameEnd) {
    return null;
  }

  return new FrameRange(finalFrameStart, finalFrameEnd);
};

/**
 * Returns whether the given frameNumber falls within the span of the element within the timeline.
 * If no frameNumber is given, the global "currentFrame" number is used for the check.
 * @param {!{playback}} state of the whole app.
 * @param {string} elementId of the element to query.
 * @param {number|undefined} frameNumber in the timeline.
 * @returns {boolean}
 */
export const isActiveInFrame = (state, elementId, frameNumber = undefined) => {
  const currentFrameNum = isNil(frameNumber) ? getCurrentFrame() : frameNumber;
  const activeRange = getActiveRange(state, elementId);
  if (isNil(activeRange)) return false;

  const { start: frameStart, end: frameEnd } = activeRange;

  // If current frame is outside bounds of this element, return false
  return frameStart <= currentFrameNum && frameEnd >= currentFrameNum;
};

export const getMetaSize = createKeyedStateSelector([getById], (element) => {
  return (
    (element && element.meta && element.meta.size) ||
    TEMPLATE_FILTER_SIZES.NONE.value
  );
});

export const getMetaFitMode = createKeyedStateSelector([getById], (element) => {
  return element && element.meta && element.meta.fitMode;
});

export const getStageBackgroundColor = (state) => {
  const {
    elements: {
      masks: {
        root: { color },
      },
    },
  } = state;
  return color;
};

/**
 * Returns list of elements that point to the given content.
 * @param {!{elements: {elements: !Array<string>, elementsById: Object<!Element>}}} state of the whole app.
 * @param {!Content} content
 * @returns {!Array<!Element>}
 */
export const getElementByContent = createSelector(
  [getAllElements, (state, content) => content],
  (allElements, content) => {
    return allElements.find((element) => {
      return element.contentId === content.contentId;
    });
  },
);

/**
 * Returns element id of an element that points to a given content id.
 * @param {!{elements: {elements: !Array<string>, elementsById: Object<!Element>}}} state of the whole app.
 * @param {!String} contentId
 * @returns {String}
 */
export const getElementIdByContentId = createKeyedStateSelector(
  [getAllElements, (state, contentId) => contentId],
  (allElements, contentId) => {
    if (isNil(contentId)) {
      return null;
    }

    const foundElement = allElements.find((element) => {
      return element.contentId === contentId;
    });

    if (isNil(foundElement)) return null;
    return foundElement.elementId;
  },
);

/**
 * Returns list of elements, filtered by whether the element is currently active in the timeline.
 * @param {!{elements, playback}} state of the whole app.
 * @param {Number} frameNumber containing elements.
 * @returns {!Array<!Element>}
 */
export const getAllActiveElements = (state, frameNumber) => {
  return getAllElements(state).filter((element) => {
    return isActiveInFrame(state, element.elementId, frameNumber);
  });
};

/**
 * Returns list of audible elements.
 * @param {!{elements, playback}} state of the whole app.
 * @returns {!Array<!Element>}
 */
export const getAllAudibleElements = createSelector(
  [getAllElements, getAllContentById, getAllMediaById],
  (allElements, contentById, mediaById) => {
    return allElements.filter((element) => {
      const content = contentById[element.contentId];
      if (
        isNil(content) ||
        !isContentAudible(content) ||
        isNil(content.mediaId)
      ) {
        return false;
      }

      const media = mediaById[content.mediaId];
      if (isNil(media)) return false;

      const { mediaType, audioServerId, processedAudio } = media;

      return hasAudio(mediaType, audioServerId, processedAudio);
    });
  },
);

/**
 * Returns list of audible elements, filtered by whether the element is currently active in the timeline.
 * @param {!{elements, playback}} state of the whole app.
 * @param {Number} frameNumber
 * @returns {!Array<!Element>}
 */
const getAllActiveAudibleElements = (state, frameNumber) => {
  return getAllAudibleElements(state).filter((element) => {
    return isActiveInFrame(state, element.elementId, frameNumber);
  });
};

/**
 * Returns true/false whether an audible element exists in the creative
 * @returns {bool}
 */
export const hasAudibleElements = createSelector(
  [getAllAudibleElements],
  (audibleElements) => isNotEmptyArray(audibleElements),
);

/**
 * Returns list of audible element Ids
 * @param {!{elements, playback}} state of the whole app.
 * @returns {!Array<!Id>}
 */
export const getAudibleElementIds = createSelector(
  [getAllAudibleElements],
  (allAudibleElements) =>
    allAudibleElements.map((element) => element.elementId),
);

/**
 * Returns the id of the element with the highest index out of the active elements with audio.
 * @param {!{content, elements, playback}} state of the whole app.
 * @returns {string|undefined} elementId
 */
export const getUppermostAudibleElementId = createSelector(
  [getAllActiveAudibleElements],
  (allActiveAudibleElements) => {
    // console.log(JSON.stringify(allActiveAudibleElements));
    return isEmptyObject(allActiveAudibleElements)
      ? undefined
      : allActiveAudibleElements[allActiveAudibleElements.length - 1].elementId;
  },
);

/**
 * Returns the given content type. Just a helpful pass-through f(x) for use with the reselect library.
 * @param {!Object} state of the whole app.
 * @param {CONTENT_TYPE|undefined} contentType
 * @returns {CONTENT_TYPE|undefined}
 */
const contentTypePassThrough = (state, contentType) => contentType;

/**
 * Returns list of contents that are referenced by an element. If contentType is specified, the list will be filtered
 * to contents that have the matching type.
 * @param {!{content, elements}} state
 * @param {CONTENT_TYPE|undefined} contentType
 * @returns {!Array<!Content>}
 */
export const getAllValidContents = createSelector(
  [contentTypePassThrough, getAllElements, getAllContentById],
  (contentType, allElements, contentById) => {
    const referencedContents = allElements
      .map((element) => {
        return contentById[element.contentId];
      })
      .filter(isNotNil);

    return isNil(contentType)
      ? referencedContents
      : referencedContents.filter(
          (content) => content.contentType === contentType,
        );
  },
);

export const isDisplayOnlyElement = createSelector(
  [getById, getParentElementId],
  (element, parentElementId) => {
    if (isNil(element)) return false;
    return (
      element.groupRole === GROUP_ROLES.DISPLAY && isNotNil(parentElementId)
    );
  },
);

// only svgs can be scale asymmetrically
export const shouldAllowAsymmetricScale = createSelector(
  [getContentType, isDisplayOnlyElement],
  (contentType, displayElement) => {
    return contentType === CONTENT_TYPE.SHAPE && !displayElement;
  },
);

export const shouldAllowSideExpand = (state, elementId) => {
  if (!elementId) {
    return false;
  }

  const contentId = getContentId(state, elementId);
  const type = ContentSelectors.getTypeById(state, contentId);
  const wrap = ContentSelectors.isTextWrap(state, contentId);
  return type === CONTENT_TYPE.TEXT && wrap;
};

export const getContentDuration = (state, elementId) => {
  const content = getContent(state, elementId);
  const contentType = getContentType(state, elementId);
  if (
    contentType !== CONTENT_TYPE.VIDEO &&
    contentType !== CONTENT_TYPE.AUDIO
  ) {
    return null;
  }
  return content.end - content.start;
};

export const isContentLoopingAllowed = createSelector(
  [getContentType],
  (contentType) => {
    return (
      contentType === CONTENT_TYPE.AUDIO || contentType === CONTENT_TYPE.VIDEO
    );
  },
);

export const getTotalFramesInRange = (state, elementId, rootEndFrame) => {
  const { frameStart, frameEnd } = getById(state, elementId);
  return isNil(rootEndFrame)
    ? 0
    : Math.min(rootEndFrame, frameEnd) - Math.max(0, frameStart);
};

export const getLoopSegmentCount = (
  state,
  elementId,
  rootEndFrame = undefined,
) => {
  if (isContentLoopingAllowed(state, elementId)) {
    const contentDuration = getContentDuration(state, elementId);
    if (contentDuration > 0) {
      const totalFramesInRange = getTotalFramesInRange(
        state,
        elementId,
        rootEndFrame,
      );
      return Math.ceil(
        totalFramesInRange / (contentDuration * PLAYBACK_FRAMES_PER_SECOND),
      );
    }
  }
  return 0;
};

export const isContentLooping = (
  state,
  elementId,
  rootEndFrame = undefined,
) => {
  return getLoopSegmentCount(state, elementId, rootEndFrame) > 1;
};

export const shouldElementTimeRangeUpdate = (
  state,
  element,
  content,
  maxDuration = undefined,
) => {
  // videos may return back with a slightly different duration than it was prior to upload
  // if it is different and the range within the timeline has not changed then adjust it
  const { frameEnd, frameStart, elementId } = element;
  const media = getMedia(state, elementId);
  const { duration: mediaDuration } = media;
  const { end: contentEnd, start: contentStart } = content;
  const originalFrameRangeDuration =
    (frameEnd - frameStart) / PLAYBACK_FRAMES_PER_SECOND;
  const originalContentDuration = contentEnd - contentStart;
  return (
    originalFrameRangeDuration === originalContentDuration &&
    maxDuration !== mediaDuration
  );
};

export const isSelectedElementImageContent = (state) => {
  const editorSelection = state.editor.selection;
  const selectedElementId = isNotNil(editorSelection)
    ? editorSelection.displayElementId
    : undefined;
  return isContentType(state, selectedElementId, CONTENT_TYPE.IMAGE);
};

export const disableKeyframeAdd = createSelector(
  [getContentType],
  (contentType) => contentType === CONTENT_TYPE.AUDIO,
);

export const isContentTypeElementGroup = createSelector(
  [getContentType],
  (contentType) => contentType === CONTENT_TYPE.ELEMENT_GROUP,
);
export const isContentTypeElementEffectGroup = createSelector(
  [getContentType],
  (contentType) => contentType === CONTENT_TYPE.ELEMENT_EFFECT_GROUP,
);
export const isGroup = createKeyedStateSelector(
  [getContentType],
  (contentType) => isGroupContentType(contentType),
);
export const getGroupRole = createKeyedStateSelector([getById], (element) => {
  if (isNil(element)) return null;
  return element.groupRole;
});

export const isLoaded = createKeyedStateSelector(
  [isGroup, getMedia],
  (isGroup, media) => {
    return isGroup || (isNotNil(media) && media.loaded);
  },
);

export const isVisibleOnCanvas = (state, elementId, frameNumber) => {
  if (
    isContentType(state, elementId, CONTENT_TYPE.AUDIO) ||
    !isLoaded(state, elementId) ||
    !isVisible(state, elementId)
  ) {
    return false;
  }

  return isActiveInFrame(state, elementId, frameNumber);
};

export const isVisibleInFrameRange = (state, elementId, frameRange) => {
  const elementFrameRange = getActiveRange(state, elementId);
  if (isNil(elementFrameRange)) return false;
  if (!FrameRange.isFrameRange(frameRange)) return false;

  return frameRange.intersects(elementFrameRange);
};

export const getGroupChildElementIds = createKeyedStateSelector(
  [getContent, isGroup],
  (content, groupContentType) => {
    if (!content || !groupContentType) return undefined;

    return content.contentElementIds;
  },
);

export const getDisplayElementIdInGroup = createSelector(
  [
    isContentTypeElementEffectGroup,
    getGroupChildElementIds,
    getAllElementsById,
  ],
  (elementEffectGroupContentType, children, allElementsById) => {
    if (!elementEffectGroupContentType || isNil(children)) return undefined;
    return children.find((elementId) => {
      const element = allElementsById[elementId];
      if (isNil(element)) return false;
      return element.groupRole === GROUP_ROLES.DISPLAY;
    });
  },
);

export const shouldAllowTranslation = createSelector(
  [getContentType, inSimpleFlow, getIsTranslationLocked, getDisplayElementIdInGroup, getAllElementsById],
  (contentType, inSimpleFlow, isTranslationLocked, displayElementIdInGroup, allElementsById) => {
    // If there is an element effect group, check the childDisplayElement for locked
    const childDisplayElement = (isNotNil(displayElementIdInGroup))
      ? allElementsById[displayElementIdInGroup]
      : null;
    const isLocked = (isNotNil(childDisplayElement))
      ? getIsTransformLockedHelper(childDisplayElement, LOCKABLE_TRANSFORMS.TRANSLATION)
      : isTranslationLocked;
    return !isLocked && (!inSimpleFlow || contentType !== CONTENT_TYPE.SHAPE);
  },
);

export const getMaskElementIdInGroup = createSelector(
  [
    isContentTypeElementEffectGroup,
    getGroupChildElementIds,
    getAllElementsById,
  ],
  (elementEffectGroupContentType, children, allElementsById) => {
    if (!elementEffectGroupContentType || isNil(children)) return undefined;
    return children.find((elementId) => {
      const element = allElementsById[elementId];
      if (isNil(element)) return false;
      return element.groupRole === GROUP_ROLES.MASK;
    });
  },
);

export const getMaskElementInGroup = createSelector(
  [getMaskElementIdInGroup, getAllElementsById],
  (maskElementId, allElementsById) => {
    if (isNil(maskElementId)) {
      return undefined;
    }
    return allElementsById[maskElementId];
  },
);

export const getElementChildrenReversed = createSelector(
  [getGroupChildElementIds],
  (elements) => {
    if (!elements) {
      return undefined;
    }
    return [...elements].reverse();
  },
);

export const getRootGroupElements = (state) => {
  return state.elements.elements.filter((elementId) =>
    isGroup(state, elementId),
  );
};

export const getDepthOfElement = (state, elementId, openedOnly = false) => {
  if (!elementId) {
    return 0;
  }
  const { contentType, contentElementIds, isOpen } = getContent(
    state,
    elementId,
  );
  if (!contentType) {
    return 0;
  }

  // if element is not a group, add 1 point!
  if (
    !isGroupContentType(contentType) ||
    (openedOnly && !isOpen) ||
    !contentElementIds ||
    !contentElementIds.length
  ) {
    return 1;
  }

  let depth = 1;
  // if has children then check each children's depth
  contentElementIds.forEach((contentElementId) => {
    const elementDepth =
      1 + getDepthOfElement(state, contentElementId, openedOnly);
    if (depth < elementDepth) {
      depth = elementDepth;
    }
  });

  return depth;
};

export const getDepthFromRoot = createSelector(
  [getRootGroupElements, (state) => state],
  (rootElements, state) => {
    let depth = 1;
    rootElements.forEach((elementId) => {
      const depthOfElement = getDepthOfElement(state, elementId, true);
      if (depth < depthOfElement) {
        depth = depthOfElement;
      }
    });

    return depth;
  },
);

export const getElementDescendantsHelper = (
  contentById,
  elementMap,
  elementId,
) => {
  if (isNil(elementId) || isNil(contentById) || isNil(elementMap)) return [];

  const element = getByIdHelper(elementMap, elementId);
  const contentId = getContentIdHelper(element);
  const content = getContentHelper(contentId, contentById);
  if (isNil(content)) {
    return [];
  }

  const { contentType, contentElementIds } = content;
  if (!contentType || !contentElementIds || !contentElementIds.length) {
    return [];
  }

  let descendants = [];
  // if has children then check each children's children
  contentElementIds.forEach((contentElementId) => {
    descendants.push(contentElementId);
    descendants = descendants.concat(
      getElementDescendantsHelper(contentById, elementMap, contentElementId),
    );
  });

  return descendants;
};

/**
 * @function getElementDescendants
 * @param {Object} state
 * @param {string} elementId
 * @return {Object} descendants
 */
export const getElementDescendants = createSelector(
  [getAllContentById, getAllElementsById, (state, elementId) => elementId],
  (contentById, elementMap, elementId) =>
    getElementDescendantsHelper(contentById, elementMap, elementId),
);

export const isAnyDescendantTransformedByUser = createSelector(
  [isGroup, getElementDescendants, getAllElementsById],
  (isGroup, descendantElementIds, elementsById) => {
    if (!isGroup) return false;
    if (!isArray(descendantElementIds)) {
      return false;
    }
    return descendantElementIds.some((elementId) => {
      const element = elementsById[elementId];
      return isNotNil(element) ? element.isTransformedByUser : false;
    });
  },
);

export const getAlphaCompositeRule = createKeyedStateSelector(
  [getById],
  (element) => {
    if (isNil(element)) return DEFAULT_ALPHA_BLEND_MODE;

    const { alphaCompositeRule } = element;
    if (
      isNotNil(alphaCompositeRule) &&
      alphaCompositeRule in ALPHA_BLEND_MODES
    ) {
      return alphaCompositeRule;
    }

    return DEFAULT_ALPHA_BLEND_MODE;
  },
);

export const getLayerByIdHelper = (element, content, media) => {
  if (isEmptyObject(element) || isEmptyObject(content)) return undefined;
  return Layer.getLayerFromReduxStateObjects(element, content, media);
};

/**
 * @function getLayerById
 * @param {Object} state
 * @param {id} elementId
 * @return {Layer}
 */
export const getLayerById = createKeyedStateSelector(
  [getById, getContent, getMedia],
  (element, content, media) => getLayerByIdHelper(element, content, media),
);

/**
 * @function getLayerByContentId
 * @param {Object} state
 * @param {contentId} contentId
 * @return {Layer}
 */
export const getLayerByContentId = createKeyedStateSelector(
  [getElementIdByContentId, getAllLayersById],
  (elementId, allLayers) => {
    if (isNil(elementId)) return null;
    if (isNil(allLayers) || allLayers.size === 0) return null;

    return allLayers.get(elementId);
  },
);

export const getLayerDescendantsById = (state, elementId) => {
  const elementDescendantIds = getElementDescendants(state, elementId);
  return elementDescendantIds.map((descendantId) =>
    getLayerById(state, descendantId),
  );
};

export const getMetaTemplateExclusion = createKeyedStateSelector(
  [getLayerById],
  (layer) => {
    return isNotNil(layer) && !!layer.excludedFromTemplate;
  },
);
